import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "../i18n";
import { api } from "../services/api";
import "./Admin.css";

export default function Admin() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("deliveries");
  const [deliveries, setDeliveries] = useState([]);
  const [presets, setPresets] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [presetName, setPresetName] = useState("");
  const [presetDesc, setPresetDesc] = useState("");
  const [presetCategory, setPresetCategory] = useState("technology");
  const [affUserId, setAffUserId] = useState("");
  const [affCode, setAffCode] = useState("");
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [presetChannels, setPresetChannels] = useState({});
  const [allChannels, setAllChannels] = useState([]);
  const [channelModal, setChannelModal] = useState(null);
  const [selectedChannelIds, setSelectedChannelIds] = useState(new Set());
  const [addingChannels, setAddingChannels] = useState(false);
  const [taskStatus, setTaskStatus] = useState({});
  const [digestWindow, setDigestWindow] = useState("morning");

  const CATEGORIES = ["technology", "finance", "international_geopolitics", "domestic_news_brazil", "other"];

  const catDotClass = (category) => {
    if (category?.includes("tech")) return "cat-dot--tech";
    if (category?.includes("finance")) return "cat-dot--finance";
    if (category?.includes("geo") || category?.includes("international")) return "cat-dot--geo";
    if (category?.includes("domestic") || category?.includes("news")) return "cat-dot--news";
    return "cat-dot--other";
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [d, p, a] = await Promise.all([api.listFailedDeliveries(), api.listPresets(), api.listAffiliates()]);
      setDeliveries(d); setPresets(p); setAffiliates(a);
      const channelsMap = {};
      await Promise.all(p.map(async (preset) => {
        try { const res = await api.getPresetChannels(preset.id); channelsMap[preset.id] = res.channels || []; } catch { channelsMap[preset.id] = []; }
      }));
      setPresetChannels(channelsMap);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRetry = async (userId, videoId) => {
    await api.retryDelivery(userId, videoId);
    setDeliveries((prev) => prev.filter((d) => !(d.user_id === userId && d.video_id === videoId)));
  };

  const handleCreatePreset = async (e) => {
    e.preventDefault();
    if (!presetName) return;
    await api.createPreset(presetName, presetDesc, presetCategory);
    setPresetName(""); setPresetDesc(""); setPresetCategory("technology");
    setPresets(await api.listPresets());
  };

  const handleRemoveChannel = async (presetId, channelId) => {
    await api.removeChannelFromPreset(presetId, channelId);
    setPresetChannels((prev) => ({
      ...prev,
      [presetId]: (prev[presetId] || []).filter((ch) => ch.id !== channelId),
    }));
  };

  const openChannelModal = async (preset) => {
    setChannelModal(preset);
    setSelectedChannelIds(new Set());
    if (allChannels.length === 0) {
      try { setAllChannels(await api.listAdminChannels()); } catch { setAllChannels([]); }
    }
  };

  const handleAddChannels = async () => {
    if (!channelModal || selectedChannelIds.size === 0) return;
    setAddingChannels(true);
    try {
      const presetId = channelModal.id;
      await Promise.all([...selectedChannelIds].map((chId) => api.addChannelToPreset(presetId, chId)));
      const res = await api.getPresetChannels(presetId);
      setPresetChannels((prev) => ({ ...prev, [presetId]: res.channels || [] }));
      setChannelModal(null);
    } catch {} finally { setAddingChannels(false); }
  };

  const toggleChannelSelection = (channelId) => {
    setSelectedChannelIds((prev) => {
      const next = new Set(prev);
      if (next.has(channelId)) next.delete(channelId);
      else next.add(channelId);
      return next;
    });
  };

  const handleCreateAffiliate = async (e) => {
    e.preventDefault();
    if (!affUserId || !affCode) return;
    await api.createAffiliate(affUserId, affCode);
    setAffUserId(""); setAffCode("");
    setAffiliates(await api.listAffiliates());
  };

  const handleViewCommissions = async (affiliate) => {
    setSelectedAffiliate(affiliate);
    setCommissions(await api.getCommissions(affiliate.id));
  };

  const TASKS = [
    { id: "poll-channels", label: t("admin.taskPollChannels"), desc: t("admin.taskPollChannelsDesc"), run: () => api.triggerPollChannels() },
    { id: "retry-failed", label: t("admin.taskRetryFailed"), desc: t("admin.taskRetryFailedDesc"), run: () => api.triggerRetryFailedDeliveries() },
    { id: "build-digests", label: t("admin.taskBuildDigests"), desc: t("admin.taskBuildDigestsDesc"), run: () => api.triggerBuildDigests(digestWindow) },
    { id: "send-digests", label: t("admin.taskSendDigests"), desc: t("admin.taskSendDigestsDesc"), run: () => api.triggerSendDigests() },
    { id: "cleanup", label: t("admin.taskCleanup"), desc: t("admin.taskCleanupDesc"), run: () => api.triggerCleanupOldData() },
  ];

  const handleRunTask = async (task) => {
    setTaskStatus((prev) => ({ ...prev, [task.id]: { running: true } }));
    try {
      const result = await task.run();
      setTaskStatus((prev) => ({ ...prev, [task.id]: { running: false, taskId: result.task_id } }));
    } catch (err) {
      setTaskStatus((prev) => ({ ...prev, [task.id]: { running: false, error: err.message || t("admin.taskFailed") } }));
    }
  };

  const TABS = [
    { id: "deliveries", label: t("admin.failedDeliveries"), count: deliveries.length },
    { id: "presets", label: t("admin.presets"), count: presets.length },
    { id: "affiliates", label: t("admin.affiliates"), count: affiliates.length },
    { id: "tasks", label: t("admin.tasks") },
  ];

  return (
    <div className="admin">
      <div className="admin-header">
        <div>
          <h1 className="admin-title">{t("admin.title")}</h1>
          <p className="admin-subtitle">{t("admin.subtitle")}</p>
        </div>
        <button className="btn-ghost btn-sm" onClick={fetchData}>{t("admin.refresh")}</button>
      </div>

      <div className="admin-tabs">
        {TABS.map((tab) => (
          <button key={tab.id} className={`admin-tab ${activeTab === tab.id ? "active" : ""}`} onClick={() => setActiveTab(tab.id)}>
            {tab.label}
            {tab.count > 0 && <span className="tab-count">{tab.count}</span>}
          </button>
        ))}
      </div>

      <div className="admin-content">
        {loading ? (
          <div className="admin-loading">{[1, 2, 3, 4].map((i) => <div key={i} className="skeleton-row" />)}</div>
        ) : (
          <>
            {activeTab === "deliveries" && (
              <div className="admin-panel">
                {deliveries.length === 0 ? (
                  <div className="admin-empty">
                    <div className="empty-icon">✓</div>
                    <h3>{t("admin.allClear")}</h3>
                    <p>{t("admin.allClearDesc")}</p>
                  </div>
                ) : (
                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>{t("admin.userId")}</th><th>{t("admin.videoId")}</th><th>{t("admin.status")}</th>
                          <th>{t("admin.attempts")}</th><th>{t("admin.errorCol")}</th><th>{t("admin.action")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deliveries.map((d) => (
                          <tr key={`${d.user_id}-${d.video_id}`}>
                            <td className="cell-id">{String(d.user_id).slice(0, 8)}...</td>
                            <td className="cell-id">{String(d.video_id).slice(0, 8)}...</td>
                            <td><span className="status-badge status-failed">{d.status}</span></td>
                            <td>{d.attempts}</td>
                            <td className="cell-error">{d.last_error || "—"}</td>
                            <td><button className="btn-primary btn-xs" onClick={() => handleRetry(d.user_id, d.video_id)}>{t("admin.retry")}</button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === "presets" && (
              <div className="admin-panel">
                <form onSubmit={handleCreatePreset} className="admin-form">
                  <h3 className="form-section-title">{t("admin.createPreset")}</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">{t("admin.name")}</label>
                      <input value={presetName} onChange={(e) => setPresetName(e.target.value)} placeholder={t("admin.namePlaceholder")} className="form-input" required />
                    </div>
                    <div className="form-group form-grow">
                      <label className="form-label">{t("admin.description")}</label>
                      <input value={presetDesc} onChange={(e) => setPresetDesc(e.target.value)} placeholder={t("admin.descPlaceholder")} className="form-input" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{t("categories.label")}</label>
                      <select value={presetCategory} onChange={(e) => setPresetCategory(e.target.value)} className="form-input">
                        {CATEGORIES.map((cat) => (
                          <option key={cat} value={cat}>{t(`categories.${cat}`)}</option>
                        ))}
                      </select>
                    </div>
                    <button type="submit" className="btn-primary btn-md form-submit-btn">{t("admin.create")}</button>
                  </div>
                </form>
                {presets.length === 0 ? (
                  <div className="admin-empty">
                    <div className="empty-icon">📦</div>
                    <h3>{t("admin.noPresets")}</h3>
                    <p>{t("admin.noPresetsDesc")}</p>
                  </div>
                ) : (
                  <div className="preset-grid">
                    {presets.map((p) => (
                      <div key={p.id} className="preset-card">
                        <div className="preset-name">{p.name}</div>
                        <div className="preset-desc">{p.description || t("admin.noDescription")}</div>
                        {(presetChannels[p.id] || []).length > 0 && (
                          <div className="preset-channels">
                            {(presetChannels[p.id] || []).map((ch) => (
                              <div key={ch.id} className="preset-channel-row">
                                <div className="preset-channel-info">
                                  <span className={`cat-dot ${catDotClass(ch.category)}`} />
                                  <span className="preset-channel-name">{ch.name}</span>
                                </div>
                                <button
                                  className="btn-remove-channel"
                                  title={t("admin.removeChannel")}
                                  onClick={() => handleRemoveChannel(p.id, ch.id)}
                                >×</button>
                              </div>
                            ))}
                          </div>
                        )}
                        <button className="btn-add-channels" onClick={() => openChannelModal(p)}>
                          + {t("admin.addChannels")}
                        </button>
                        <div className="preset-id">{String(p.id).slice(0, 8)}...</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === "affiliates" && (
              <div className="admin-panel">
                <form onSubmit={handleCreateAffiliate} className="admin-form">
                  <h3 className="form-section-title">{t("admin.createAffiliate")}</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">{t("admin.userIdLabel")}</label>
                      <input value={affUserId} onChange={(e) => setAffUserId(e.target.value)} placeholder="UUID" className="form-input" required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{t("admin.referralCode")}</label>
                      <input value={affCode} onChange={(e) => setAffCode(e.target.value)} placeholder={t("admin.codePlaceholder")} className="form-input" required />
                    </div>
                    <button type="submit" className="btn-primary btn-md form-submit-btn">{t("admin.create")}</button>
                  </div>
                </form>
                {affiliates.length === 0 ? (
                  <div className="admin-empty">
                    <div className="empty-icon">🤝</div>
                    <h3>{t("admin.noAffiliates")}</h3>
                    <p>{t("admin.noAffiliatesDesc")}</p>
                  </div>
                ) : (
                  <div className="affiliates-list">
                    {affiliates.map((a) => (
                      <div key={a.id} className="affiliate-row">
                        <div className="affiliate-info">
                          <div className="affiliate-code">{a.code}</div>
                          <div className="affiliate-rate">{(Number(a.commission_rate) * 100).toFixed(0)}% {t("admin.commission")}</div>
                        </div>
                        <button className="btn-ghost btn-xs" onClick={() => handleViewCommissions(a)}>{t("admin.viewCommissions")}</button>
                      </div>
                    ))}
                  </div>
                )}

                {selectedAffiliate && (
                  <div className="commissions-panel">
                    <div className="commissions-header">
                      <h3>{t("admin.commissions")} — {selectedAffiliate.code}</h3>
                      <button className="btn-ghost btn-xs" onClick={() => setSelectedAffiliate(null)}>{t("admin.close")}</button>
                    </div>
                    {commissions.length === 0 ? (
                      <p className="commissions-empty">{t("admin.noCommissions")}</p>
                    ) : (
                      <table className="admin-table">
                        <thead><tr><th>{t("admin.period")}</th><th>{t("admin.amount")}</th><th>{t("admin.status")}</th></tr></thead>
                        <tbody>
                          {commissions.map((c, i) => (
                            <tr key={i}>
                              <td>{c.period_start} — {c.period_end}</td>
                              <td>R$ {c.amount}</td>
                              <td><span className={`status-badge status-${c.status}`}>{c.status}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            )}
            {activeTab === "tasks" && (
              <div className="admin-panel">
                <div className="task-grid">
                  {TASKS.map((task) => {
                    const status = taskStatus[task.id] || {};
                    return (
                      <div key={task.id} className="task-card">
                        <div className="task-card-header">
                          <h3 className="task-card-title">{task.label}</h3>
                        </div>
                        <p className="task-card-desc">{task.desc}</p>
                        {task.id === "build-digests" && (
                          <div className="task-card-param">
                            <label className="form-label">{t("admin.window")}</label>
                            <select value={digestWindow} onChange={(e) => setDigestWindow(e.target.value)} className="form-input form-input-sm">
                              <option value="morning">{t("admin.morning")}</option>
                              <option value="evening">{t("admin.evening")}</option>
                            </select>
                          </div>
                        )}
                        <button
                          className="btn-primary btn-sm task-run-btn"
                          disabled={status.running}
                          onClick={() => handleRunTask(task)}
                        >
                          {status.running ? t("admin.running") : t("admin.run")}
                        </button>
                        {status.taskId && (
                          <div className="task-result task-result--success">
                            {t("admin.taskTriggered")} &middot; <span className="task-id">{status.taskId}</span>
                          </div>
                        )}
                        {status.error && (
                          <div className="task-result task-result--error">
                            {status.error}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {channelModal && (
        <div className="acm-backdrop" onClick={() => setChannelModal(null)}>
          <div className="acm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="acm-header">
              <h2>{t("admin.addChannelsTo")} <em>{channelModal.name}</em></h2>
              <button className="acm-close" onClick={() => setChannelModal(null)}>&times;</button>
            </div>
            <div className="acm-body">
              {allChannels.map((ch) => (
                <div
                  key={ch.id}
                  className={`acm-row ${selectedChannelIds.has(ch.id) ? "selected" : ""}`}
                  onClick={() => toggleChannelSelection(ch.id)}
                >
                  <div className="acm-checkbox">✓</div>
                  <div className="acm-avatar">
                    {ch.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()}
                  </div>
                  <div className="acm-info">
                    <div className="acm-name">{ch.name}</div>
                    <div className="acm-category">
                      <span className={`cat-dot ${catDotClass(ch.category)}`} />
                      {ch.category?.replace(/_/g, " ")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="acm-footer">
              <div className="selected-count">
                {t("admin.selectedCount", { count: selectedChannelIds.size })}
              </div>
              <div className="acm-actions">
                <button className="btn-modal-ghost" onClick={() => setChannelModal(null)}>{t("dashboard.cancel")}</button>
                <button
                  className="btn-modal-primary"
                  disabled={selectedChannelIds.size === 0 || addingChannels}
                  onClick={handleAddChannels}
                >
                  {addingChannels ? t("admin.adding") : `${t("admin.addSelected")} (${selectedChannelIds.size})`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
